<div class="new-project-dialog">
  <h2 mat-dialog-title>Create New Project</h2>

  <mat-dialog-content>
    @if (!projectCreated) {
      <div class="form-container">
        <!-- Step 1: Human-readable project name -->
        <mat-form-field appearance="outline" class="full-width" subscriptSizing="dynamic">
          <mat-label>Project Name</mat-label>
          <input
            matInput
            data-testid="new-project-name"
            [formControl]="projectNameControl"
            [errorStateMatcher]="immediateErrorStateMatcher"
            placeholder="Property or development name"
            [readOnly]="loading"
            [attr.aria-disabled]="loading"
            required
            aria-describedby="project-name-guidance"
          />
          <button
            mat-icon-button
            matSuffix
            type="button"
            aria-label="About Project Name"
            aria-haspopup="dialog"
            (click)="openProjectNameInfo()"
          >
            <mat-icon>info</mat-icon>
          </button>
          <mat-hint id="project-name-guidance">Use a recognizable property or development name.</mat-hint>
          @if ((projectNameControl.touched || projectNameControl.dirty) && projectNameControl.hasError('required')) {
            <mat-error id="project-name-error" aria-live="polite" aria-atomic="true">
              Project Name is required.
            </mat-error>
          }
        </mat-form-field>

        <!-- Step 2: Stable technical project ID -->
        <mat-form-field appearance="outline" class="full-width" subscriptSizing="dynamic">
          <mat-label>Project ID</mat-label>
          <input
            matInput
            data-testid="new-project-id"
            [formControl]="projectIdControl"
            [errorStateMatcher]="immediateErrorStateMatcher"
            (input)="onProjectIdInput()"
            placeholder="Stable lowercase address-based identifier"
            [readOnly]="loading"
            [attr.aria-disabled]="loading"
            required
            maxlength="128"
            aria-describedby="project-id-guidance"
          />
          <button
            mat-icon-button
            matSuffix
            type="button"
            aria-label="About Project ID"
            aria-haspopup="dialog"
            (click)="openProjectIdInfo()"
          >
            <mat-icon>info</mat-icon>
          </button>
          <mat-hint id="project-id-guidance">Use a stable lowercase ID for URLs and storage.</mat-hint>
          @if ((projectIdControl.touched || projectIdControl.dirty) && projectIdError) {
            <mat-error id="project-id-error" aria-live="polite" aria-atomic="true">
              {{ projectIdError }}
            </mat-error>
          }
        </mat-form-field>

        <!-- Step 3: Upload choice -->
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Architectural Plans</mat-label>
          <mat-select data-testid="new-project-upload-choice" [(ngModel)]="uploadChoice" [disabled]="loading">
            <mat-option value="now">Upload now</mat-option>
            <mat-option value="later">Upload later</mat-option>
          </mat-select>
        </mat-form-field>

        <!-- Step 4: File Upload (shown when uploading now) -->
        @if (uploadChoice === 'now') {
          <div class="file-upload-section">
            <label class="file-upload-label" for="file-input">Select PDF File</label>
            <div
              class="file-upload-area"
              [class.has-file]="selectedFile"
              (click)="triggerFileInput()"
              (keydown.enter)="triggerFileInput()"
              (keydown.space)="triggerFileInput(); $event.preventDefault()"
              tabindex="0"
              role="button"
              aria-label="Click to select PDF file"
            >
              <input
                #fileInput
                id="file-input"
                type="file"
                accept=".pdf"
                (change)="onFileSelected($event)"
                style="display: none"
              />
              @if (!selectedFile) {
                <div class="file-upload-placeholder">
                  <mat-icon>cloud_upload</mat-icon>
                  <span>Click to select PDF file</span>
                </div>
              }
              @if (selectedFile) {
                <div class="file-upload-selected">
                  <mat-icon>description</mat-icon>
                  <div class="file-info">
                    <div class="file-name">{{ selectedFile.name }}</div>
                    <div class="file-size">{{ (selectedFile.size / 1024 / 1024).toFixed(2) }} MB</div>
                  </div>
                  <button mat-icon-button (click)="clearFile(); $event.stopPropagation()">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              }
            </div>
          </div>

          <!-- Step 5: Pages to process (shown after file is selected) -->
          @if (selectedFile) {
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Pages to Process</mat-label>
              <mat-select [(ngModel)]="pages" [disabled]="loading">
                <mat-option value="all">All Pages</mat-option>
                <mat-option value="custom">Custom Pages</mat-option>
                <mat-option value="none">None (upload only, ingest later)</mat-option>
              </mat-select>
            </mat-form-field>
            @if (pages === 'custom') {
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Page Numbers</mat-label>
                <input matInput [(ngModel)]="customPages" placeholder="1,2,3,5-7,10" [disabled]="loading" required />
                <mat-hint>Enter comma-separated page numbers or ranges (e.g., 1,2,3-5,7)</mat-hint>
              </mat-form-field>
            }
          }
        }

        @if (loading) {
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
        }
      </div>
    }
  </mat-dialog-content>

  <mat-dialog-actions class="dialog-actions">
    <button mat-button (click)="onCancel()">
      {{ projectCreated ? 'Close' : 'Cancel' }}
    </button>
    <div class="spacer"></div>
    @if (!projectCreated) {
      <button
        mat-raised-button
        data-testid="new-project-submit"
        color="primary"
        (click)="onConfirm()"
        [disabled]="!isValid || loading"
        [attr.aria-disabled]="loading"
        [style.pointer-events]="loading ? 'none' : 'auto'"
      >
        <mat-icon>{{ loading ? 'hourglass_empty' : 'add' }}</mat-icon>
        {{ loading ? 'Creating...' : 'Create Project' }}
      </button>
    }
  </mat-dialog-actions>
</div>
